/**
 * Admin Roles Screen
 *
 * Role management with:
 * - View all roles and their granted permissions
 * - Create custom roles
 * - Edit the permissions included in each role
 * - See which users/groups have each role
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Shield,
  RefreshCw,
  X,
  Search,
  Users,
  User,
  ChevronDown,
  ChevronUp,
  Package,
  Layers,
  GitBranch,
  Lock,
  List,
  SlidersHorizontal,
  BarChart2,
  Tag,
  ArrowLeftRight,
  Network,
  Settings,
  LayoutGrid,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { getSupabaseClient } from '../../../lib/supabaseClient';
import { createRoleAssignment } from '../../../lib/rolesService';
import { updateRolePermissions as updateRolePermissionsRpc } from '../../../lib/delegationRpc';
import { resolveAdminScope, fetchUsersForScope } from '../../../lib/adminScope';
import type { ScopeKind } from '../../../types/prioritization/permissions';
import {
  PERMISSION_DEFINITIONS,
  getPermissionsByCategory,
  type PermissionAction,
  type PermissionCategory,
} from '../../../types/prioritization/permissions';
import { roleDefinitions, type ActorRole, type ScopeReach } from '../../../types/prioritization/roles';
import { RoleBadge } from '../../../components/prioritization/RoleBadge';
import { InlineGroupCreator } from './_InlineGroupCreator';

// ============================================================================
// Types
// ============================================================================

interface RoleWithDetails {
  id: string;
  name?: string;
  label: string;
  description: string | null;
  permissionTier: number;
  isSystem: boolean;
  permissions: PermissionAction[];
  scopeReach: ScopeReach;
  userCount: number;
  groupCount: number;
  createdAt: string;
}

interface RoleAssignee {
  id: string;
  subjectType: 'user' | 'group';
  subjectId: string;
  subjectName: string;
  scopeKind: string;
  scopeId: string;
}

// ============================================================================
// Constants
// ============================================================================

const PERMISSION_CATEGORIES: {
  id: PermissionCategory;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { id: 'items',     label: 'Items',           description: 'Create, triage, edit, view, and delete prioritization items',              icon: Package },
  { id: 'process',   label: 'Cycles & Phases',  description: 'Manage cycles, advance phases, and publish results',                      icon: Layers },
  { id: 'tracks',    label: 'Tracks', description: 'Create and configure Tracks within cycles',                  icon: GitBranch },
  { id: 'session',   label: 'Sessions',         description: 'Facilitate, participate in, and publish prioritization sessions',         icon: Users },
  { id: 'pools',     label: 'Pool Governance',  description: 'Select, lock, and reopen item pools',                                     icon: Lock },
  { id: 'lists',     label: 'Item Pools',       description: 'Create and manage item pools and cut lines',                              icon: List },
  { id: 'criteria',  label: 'Criteria',         description: 'Define, select, weight, lock, and reopen criteria',                       icon: SlidersHorizontal },
  { id: 'values',    label: 'Values & Ranges',  description: 'Propose, accept, and override values; define and approve value ranges',   icon: BarChart2 },
  { id: 'attributes',label: 'Attributes',       description: 'Define attribute schemas and assign attribute owners',                    icon: Tag },
  { id: 'catchball', label: 'Catchball',        description: 'Request, propose, return, accept, and reject catchball negotiations',     icon: ArrowLeftRight },
  { id: 'workflow',  label: 'Workflows',        description: 'Create, edit, archive, and assign workflows and templates',               icon: Network },
  { id: 'analytics', label: 'Analytics',        description: 'View and export analytics and reports',                                  icon: LayoutGrid },
  { id: 'admin',     label: 'Administration',   description: 'Manage users, groups, roles, policies, and system settings',             icon: Settings },
];

const SCOPE_REACH_OPTIONS: { value: ScopeReach; label: string; description: string }[] = [
  {
    value: 'none',
    label: 'Explicit grant only',
    description: 'Permissions apply only at the exact scope where this role is assigned. Users must be granted this role at each specific org unit, cycle, or resource where they need access.',
  },
  {
    value: 'children',
    label: 'Cascade to descendants',
    description: 'Permissions apply at the assigned org unit and all org units beneath it. Useful for team leads or division-level roles.',
  },
  {
    value: 'all',
    label: 'Apply everywhere',
    description: 'Permissions apply across the entire workspace regardless of where assigned. Reserved for administrator roles.',
  },
];

// ============================================================================
// Main Component
// ============================================================================

export default function AdminRoles() {
  const currentRole = useUserStore((state) => state.currentRole);
  const assignedRoleNames = useUserStore((state) => state.assignedRoleNames);
  const { switchRole } = useUserStore();
  const currentWorkspaceId = useTenantStore((state) => state.currentWorkspaceId);
  const currentTenantId = useTenantStore((state) => state.currentTenantId);
  const workspaceResolutionComplete = useTenantStore((state) => state.workspaceResolutionComplete);
  const adminScope = useMemo(
    () => resolveAdminScope(currentRole, assignedRoleNames),
    [currentRole, assignedRoleNames],
  );

  const [roles, setRoles] = useState<RoleWithDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState<RoleWithDetails | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssigneesModal, setShowAssigneesModal] = useState(false);
  const [expandedRole, setExpandedRole] = useState<string | null>(null);
  const [showRoleDefinitions, setShowRoleDefinitions] = useState(false);

  const actorRoles: ActorRole[] = ['workspace-admin', 'tenant-admin', 'platform-admin'];

  // ========================================================================
  // Data Fetching
  // ========================================================================

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const { currentWorkspaceId: wsId, currentTenantId: tenantId } = useTenantStore.getState();
      const supabase = getSupabaseClient();
      let rolesQuery = supabase.from('roles').select('*');
      if (wsId) {
        const tenantPart = tenantId ? `,workspace_id.eq.${tenantId}` : '';
        rolesQuery = rolesQuery.or(`is_system.eq.true,workspace_id.eq.${wsId},workspace_id.is.null${tenantPart}`);
      } else {
        rolesQuery = rolesQuery.eq('is_system', true);
      }

      // Match AdminUsers: include workspace-wide '*' assignments in assignee counts
      let raQuery = supabase.from('role_assignments').select('*');
      if (wsId && tenantId) {
        raQuery = raQuery.or(`scope_id.eq.${wsId},scope_id.eq.${tenantId},scope_id.eq.*`);
      } else if (wsId) {
        raQuery = raQuery.or(`scope_id.eq.${wsId},scope_id.eq.*`);
      } else if (tenantId) {
        raQuery = raQuery.or(`scope_id.eq.${tenantId},scope_id.eq.*`);
      }

      const [rolesResult, rolePermissionsResult, roleAssignmentsResult] = await Promise.all([
        rolesQuery,
        supabase.from('role_permissions').select('*'),
        raQuery,
      ]);

      if (rolesResult.error) throw new Error(rolesResult.error.message);

      const roleRows = [...(rolesResult.data || [])];
      const seenRoleIds = new Set(roleRows.map((r: { id: string }) => r.id));
      const assignmentRoleIds = new Set(
        (roleAssignmentsResult.data || []).map((ra: { role_id: string }) => ra.role_id),
      );
      const missingRoleIds = [...assignmentRoleIds].filter((id) => id && !seenRoleIds.has(id));
      if (missingRoleIds.length > 0) {
        const { data: extraRoles, error: extraRolesErr } = await supabase
          .from('roles')
          .select('*')
          .in('id', missingRoleIds);
        if (!extraRolesErr && extraRoles) {
          for (const r of extraRoles) {
            roleRows.push(r);
            seenRoleIds.add(r.id);
          }
        }
      }

      const permissionsByRole = new Map<string, PermissionAction[]>();
      (rolePermissionsResult.data || []).forEach(rp => {
        if (!permissionsByRole.has(rp.role_id)) permissionsByRole.set(rp.role_id, []);
        permissionsByRole.get(rp.role_id)!.push(rp.permission as PermissionAction);
      });

      const userCountByRole = new Map<string, number>();
      const groupCountByRole = new Map<string, number>();
      (roleAssignmentsResult.data || []).forEach(ra => {
        if (ra.subject_type === 'user') {
          userCountByRole.set(ra.role_id, (userCountByRole.get(ra.role_id) || 0) + 1);
        } else if (ra.subject_type === 'group') {
          groupCountByRole.set(ra.role_id, (groupCountByRole.get(ra.role_id) || 0) + 1);
        }
      });

      const rolesWithDetails: RoleWithDetails[] = roleRows.map(r => ({
        id: r.id,
        name: r.name,
        label: r.display_name || r.label || r.name,
        description: r.description,
        permissionTier: r.permission_tier || (r.tiers ? Math.max(...r.tiers) : 0),
        isSystem: r.is_system || false,
        permissions: permissionsByRole.get(r.id) || [],
        scopeReach: (r.cascade_mode as ScopeReach) || 'none',
        userCount: userCountByRole.get(r.id) || 0,
        groupCount: groupCountByRole.get(r.id) || 0,
        createdAt: r.created_at,
      }));

      rolesWithDetails.sort((a, b) => b.permissionTier - a.permissionTier);
      setRoles(rolesWithDetails);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!workspaceResolutionComplete) return;
    void fetchData();
  }, [currentWorkspaceId, currentTenantId, workspaceResolutionComplete, assignedRoleNames]);

  // ========================================================================
  // CRUD Operations
  // ========================================================================

  const createRole = async (data: { id: string; label: string; description?: string; permissions: PermissionAction[]; scopeReach: ScopeReach }) => {
    try {
      if (!currentWorkspaceId) {
        alert('Please select a workspace before creating a custom role.');
        return;
      }
      const supabase = getSupabaseClient();
      const roleId = data.id.toLowerCase().replace(/[_\s]+/g, '-');
      const { data: insertedRole, error: roleError } = await supabase.from('roles').insert({
        name: roleId,
        display_name: data.label,
        description: data.description || null,
        is_system: false,
        role_type: 'custom',
        workspace_id: currentWorkspaceId,
        cascade_mode: data.scopeReach,
      }).select().single();

      if (roleError) throw roleError;

      if (data.permissions.length > 0 && insertedRole) {
        const permResult = await updateRolePermissionsRpc({ roleId: insertedRole.id, permissions: data.permissions });
        if (!permResult.success) throw new Error(permResult.error || 'Permission assignment denied');
      }

      await fetchData();
      setShowCreateModal(false);
    } catch (err) {
      alert(`Failed to create role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const updateRole = async (id: string, data: { label: string; description?: string; permissions: PermissionAction[]; scopeReach: ScopeReach }) => {
    try {
      const supabase = getSupabaseClient();
      const { error: roleError } = await supabase.from('roles').update({
        display_name: data.label,
        description: data.description || null,
        cascade_mode: data.scopeReach,
      }).eq('id', id);

      if (roleError) throw roleError;

      const permResult = await updateRolePermissionsRpc({ roleId: id, permissions: data.permissions });
      if (!permResult.success) throw new Error(permResult.error || 'Permission update denied');

      await fetchData();
      setShowEditModal(false);
      setSelectedRole(null);
    } catch (err) {
      alert(`Failed to update role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const deleteRole = async (id: string) => {
    if (!confirm('Delete this role? All assignments will be removed.')) return;
    try {
      const supabase = getSupabaseClient();
      const { error } = await supabase.from('roles').delete().eq('id', id);
      if (error) throw error;
      await fetchData();
    } catch (err) {
      alert(`Failed to delete role: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  const filteredRoles = roles.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return r.label.toLowerCase().includes(q) || r.id.toLowerCase().includes(q);
  });

  // ========================================================================
  // Access Gate
  // ========================================================================

  if (adminScope === 'none') {
    return (
      <div className="p-8">
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] p-4">
          <h2 className="text-lg font-semibold text-[var(--coral)] mb-2">Access Denied</h2>
          <p className="text-sm text-[var(--coral)] mb-4">
            You must be a Tenant Admin, Workspace Admin, or Platform Admin to access this page.
          </p>
          <button onClick={() => switchRole('platform-admin')} className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)]">
            Switch to Platform Admin
          </button>
        </div>
      </div>
    );
  }

  // ========================================================================
  // Render
  // ========================================================================

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        icon={<Shield className="h-4 w-4 text-primary-600" />}
        iconBg="bg-primary-50"
        title="Roles"
        subtitle="Create and manage roles, then choose which permissions each role grants."
        actions={
          <div className="flex gap-2">
            <button onClick={() => setShowCreateModal(true)} className="btn btn-primary flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Create Role
            </button>
          </div>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Roles',       value: roles.length,                          color: 'text-[var(--text-pri)]' },
          { label: 'System Roles',      value: roles.filter(r => r.isSystem).length,  color: 'text-[var(--text-ter)]' },
          { label: 'Custom Roles',      value: roles.filter(r => !r.isSystem).length, color: 'text-[var(--text-ter)]' },
          { label: 'Total Permissions', value: Object.keys(PERMISSION_DEFINITIONS).length, color: 'text-[var(--green)] dark:text-[var(--text-ter)]' },
        ].map(s => (
          <div key={s.label} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-4">
            <div className="text-sm text-[var(--text-ter)] mb-1">{s.label}</div>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Role Definitions Reference */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <button
          onClick={() => setShowRoleDefinitions(!showRoleDefinitions)}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--surface)] transition-colors"
        >
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-[var(--text-ter)]" />
            <span className="text-sm font-medium text-[var(--text-sec)]">Role Definitions Reference</span>
          </div>
          {showRoleDefinitions ? <ChevronUp className="h-4 w-4 text-[var(--text-ter)]" /> : <ChevronDown className="h-4 w-4 text-[var(--text-ter)]" />}
        </button>
        {showRoleDefinitions && (
          <div className="border-t border-[var(--rule)] divide-y divide-[var(--rule)]">
            {actorRoles.map(role => {
              const def = roleDefinitions[role];
              if (!def) return null;
              return (
                <div key={role} className="flex items-start gap-3 px-4 py-3">
                  <span className="text-lg mt-0.5">{def.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-semibold ${def.color}`}>{def.name}</span>
                      <RoleBadge role={role} size="sm" showIcon={false} />
                      <ScopeReachBadge mode={def.scopeReach} />
                    </div>
                    <p className="text-xs text-[var(--text-ter)] mt-0.5">{def.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
        <input
          type="text"
          placeholder="Search roles…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>

      {error && (
        <div className="bg-[var(--coral-dim)] border border-[var(--coral)] px-4 py-3 text-sm text-[var(--coral)]">{error}</div>
      )}

      {/* Roles List */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-[var(--text-ter)]">
            <RefreshCw className="h-5 w-5 animate-spin" />
            <span className="text-sm">Loading roles…</span>
          </div>
        ) : filteredRoles.length === 0 ? (
          <div className="text-center py-16 text-sm text-[var(--text-ter)]">
            {searchQuery ? 'No roles match your search.' : 'No roles found.'}
          </div>
        ) : (
          <div className="divide-y divide-[var(--rule)]">
            {filteredRoles.map(role => {
              const isExpanded = expandedRole === role.id;
              return (
                <div key={role.id}>
                  {/* Row */}
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-[var(--surface)]">
                    <button
                      onClick={() => setExpandedRole(isExpanded ? null : role.id)}
                      className="text-[var(--text-ter)] hover:text-[var(--text-ter)] flex-shrink-0"
                      aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>

                    <div className={`p-1.5  flex-shrink-0 ${role.isSystem ? 'bg-[var(--slate-dim)]' : 'bg-[var(--slate-dim)]'}`}>
                      <Shield className={`h-4 w-4 ${role.isSystem ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`} />
                    </div>

                    {/* Grows to fill the row; on a phone the counts and actions wrap below it */}
                    <div className="min-w-0 flex-[1_1_12rem]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-[var(--text-pri)]">{role.label}</span>
                        {role.isSystem && (
                          <span className="text-xs bg-[var(--hover-tint)] text-[var(--text-ter)] px-1.5 py-0.5 rounded">System</span>
                        )}
                        <ScopeReachBadge mode={role.scopeReach} />
                      </div>
                      {role.description && (
                        <p className="text-xs text-[var(--text-ter)] mt-0.5 truncate">{role.description}</p>
                      )}
                    </div>

                    <div className="ml-auto flex items-center gap-4 flex-shrink-0">
                      <span className="text-xs text-[var(--text-ter)] tabular-nums">{role.permissions.length} perms</span>
                      <div className="flex items-center gap-3 text-xs text-[var(--text-ter)]">
                        <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{role.userCount}</span>
                        <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{role.groupCount}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => { setSelectedRole(role); setShowAssigneesModal(true); }}
                          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)]"
                          title="View Assignees"
                        >
                          <Users className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => { setSelectedRole(role); setShowEditModal(true); }}
                          className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)]"
                          title="Edit Role"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        {!role.isSystem && (
                          <button
                            onClick={() => deleteRole(role.id)}
                            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] rounded hover:bg-[var(--coral-dim)]"
                            title="Delete Role"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded: permission category breakdown */}
                  {isExpanded && (
                    <div className="px-12 pb-4 bg-[var(--surface)] border-t border-[var(--rule)]">
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 pt-3">
                        {PERMISSION_CATEGORIES.map(cat => {
                          const catPerms = getPermissionsByCategory(cat.id);
                          const granted = catPerms.filter(p => role.permissions.includes(p.id));
                          if (granted.length === 0) return null;
                          const Icon = cat.icon;
                          return (
                            <div key={cat.id} className="bg-[var(--bg-raised)] border border-[var(--rule)] p-3">
                              <div className="flex items-center gap-1.5 mb-2">
                                <Icon className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
                                <span className="text-xs font-medium text-[var(--text-sec)] truncate">{cat.label}</span>
                                <span className="ml-auto text-xs font-medium text-[var(--text-ter)] tabular-nums flex-shrink-0">
                                  {granted.length}/{catPerms.length}
                                </span>
                              </div>
                              <div className="space-y-1">
                                {catPerms.map(p => (
                                  <div key={p.id} className="flex items-center gap-1.5">
                                    <div className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${role.permissions.includes(p.id) ? 'bg-[var(--green)]' : 'bg-[var(--hover-tint)]'}`} />
                                    <span className={`text-xs truncate ${role.permissions.includes(p.id) ? 'text-[var(--text-sec)]' : 'text-[var(--text-ter)]'}`}>
                                      {p.label}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                        {PERMISSION_CATEGORIES.every(cat => getPermissionsByCategory(cat.id).filter(p => role.permissions.includes(p.id)).length === 0) && (
                          <p className="col-span-full text-xs text-[var(--text-ter)] py-2">No permissions granted.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      {showCreateModal && (
        <RoleFormModal
          title="Create Role"
          onSubmit={createRole}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {showEditModal && selectedRole && (
        <RoleFormModal
          title="Edit Role"
          initialData={selectedRole}
          onSubmit={data => updateRole(selectedRole.id, data)}
          onClose={() => { setShowEditModal(false); setSelectedRole(null); }}
        />
      )}

      {showAssigneesModal && selectedRole && (
        <RoleAssigneesModal
          role={selectedRole}
          onClose={() => { setShowAssigneesModal(false); setSelectedRole(null); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Cascade Mode Badge
// ============================================================================

function ScopeReachBadge({ mode }: { mode: ScopeReach }) {
  if (mode === 'all') return (
    <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-1.5 py-0.5 rounded font-medium">Everywhere</span>
  );
  if (mode === 'children') return (
    <span className="text-xs bg-[var(--slate-dim)] text-[var(--text-sec)] px-1.5 py-0.5 rounded font-medium">Cascades down</span>
  );
  return (
    <span className="text-xs bg-[var(--hover-tint)] text-[var(--text-ter)] px-1.5 py-0.5 rounded font-medium">Explicit grant</span>
  );
}

// ============================================================================
// Role Form Modal — two-panel permission picker
// ============================================================================

interface RoleFormModalProps {
  title: string;
  initialData?: RoleWithDetails;
  onSubmit: (data: { id: string; label: string; description?: string; permissions: PermissionAction[]; scopeReach: ScopeReach }) => void;
  onClose: () => void;
}

function RoleFormModal({ title, initialData, onSubmit, onClose }: RoleFormModalProps) {
  const [id, setId] = useState(initialData?.id || '');
  const [label, setLabel] = useState(initialData?.label || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [scopeReach, setCascadeMode] = useState<ScopeReach>(initialData?.scopeReach ?? 'none');
  const [selectedPermissions, setSelectedPermissions] = useState<Set<PermissionAction>>(
    new Set(initialData?.permissions || [])
  );
  const [activeCategory, setActiveCategory] = useState<PermissionCategory>(PERMISSION_CATEGORIES[0].id);

  const isEditing = !!initialData;
  const activeCatDef = PERMISSION_CATEGORIES.find(c => c.id === activeCategory)!;
  const activeCatPerms = getPermissionsByCategory(activeCategory);
  const activeCatSelected = activeCatPerms.filter(p => selectedPermissions.has(p.id));
  const allActiveSelected = activeCatSelected.length === activeCatPerms.length;

  const toggle = (permId: PermissionAction) => {
    const next = new Set(selectedPermissions);
    if (next.has(permId)) next.delete(permId); else next.add(permId);
    setSelectedPermissions(next);
  };

  const toggleCategory = () => {
    const next = new Set(selectedPermissions);
    activeCatPerms.forEach(p => allActiveSelected ? next.delete(p.id) : next.add(p.id));
    setSelectedPermissions(next);
  };

  const handleSubmit = () => {
    if (!id.trim() || !label.trim()) { alert('ID and Label are required'); return; }
    onSubmit({ id, label, description, permissions: Array.from(selectedPermissions), scopeReach });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-4xl flex flex-col h-[85vh]">

        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)] flex-shrink-0">
          <h2 className="text-base font-semibold text-[var(--text-pri)]">{title}</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Basic fields */}
        <div className="px-6 py-4 border-b border-[var(--rule)] flex-shrink-0 space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">ID *</label>
              <input
                type="text"
                value={id}
                onChange={e => setId(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                disabled={isEditing}
                placeholder="ROLE_NAME"
                className="w-full border border-[var(--rule)] px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500 disabled:bg-[var(--surface)] disabled:text-[var(--text-ter)]"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">Label *</label>
              <input
                type="text"
                value={label}
                onChange={e => setLabel(e.target.value)}
                placeholder="Human-readable name"
                className="w-full border border-[var(--rule)] px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1">Description</label>
            <input
              type="text"
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What this role is for…"
              className="w-full border border-[var(--rule)] px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Scope Cascade — compact inline radio */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide mb-1.5">
              Scope Cascade
              <span className="ml-1.5 text-[var(--text-ter)] normal-case font-normal">— how permissions propagate through the org hierarchy</span>
            </label>
            <div className="flex gap-2 flex-wrap">
              {SCOPE_REACH_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-2  border px-3 py-2 cursor-pointer transition-colors flex-1 min-w-[180px] ${
                    scopeReach === opt.value ? 'border-[var(--slate)] bg-[var(--slate-dim)]' : 'border-[var(--rule)] hover:border-[var(--rule)]'
                  }`}
                >
                  <input
                    type="radio"
                    name="scopeReach"
                    value={opt.value}
                    checked={scopeReach === opt.value}
                    onChange={() => setCascadeMode(opt.value)}
                    className="mt-0.5 text-[var(--text-ter)] focus:ring-blue-500 flex-shrink-0"
                  />
                  <div>
                    <div className="text-xs font-medium text-[var(--text-pri)]">{opt.label}</div>
                    <div className="text-xs text-[var(--text-ter)] mt-0.5 leading-snug">{opt.description}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Two-panel permission picker */}
        <div className="flex flex-1 min-h-0 overflow-hidden">

          {/* Left — category nav */}
          <nav className="w-52 flex-shrink-0 border-r border-[var(--rule)] overflow-y-auto py-2">
            {PERMISSION_CATEGORIES.map(cat => {
              const catPerms = getPermissionsByCategory(cat.id);
              const selected = catPerms.filter(p => selectedPermissions.has(p.id)).length;
              const isActive = activeCategory === cat.id;
              const Icon = cat.icon;

              let dot = 'bg-[var(--hover-tint)]';
              if (selected === catPerms.length) dot = 'bg-[var(--green)]';
              else if (selected > 0) dot = 'bg-[var(--mustard)]';

              return (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                    isActive ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]' : 'text-[var(--text-sec)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)]'
                  }`}
                >
                  <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${isActive ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`} />
                  <span className="flex-1 text-xs font-medium truncate">{cat.label}</span>
                  <span className={`text-xs tabular-nums ${isActive ? 'text-[var(--text-ter)]' : 'text-[var(--text-ter)]'}`}>
                    {selected}/{catPerms.length}
                  </span>
                  <div className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${dot}`} />
                </button>
              );
            })}
          </nav>

          {/* Right — permission list for active category */}
          <div className="flex-1 overflow-y-auto">
            {/* Category header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-[var(--rule)] sticky top-0 bg-[var(--bg-raised)] z-10">
              <div>
                <div className="flex items-center gap-2">
                  <activeCatDef.icon className="h-4 w-4 text-[var(--text-ter)]" />
                  <span className="text-sm font-semibold text-[var(--text-pri)]">{activeCatDef.label}</span>
                  <span className="text-xs text-[var(--text-ter)] tabular-nums">
                    {activeCatSelected.length} / {activeCatPerms.length} selected
                  </span>
                </div>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">{activeCatDef.description}</p>
              </div>
              <button
                type="button"
                onClick={toggleCategory}
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium flex-shrink-0"
              >
                {allActiveSelected ? 'Deselect all' : 'Select all'}
              </button>
            </div>

            {/* Permission rows */}
            <div className="divide-y divide-[var(--rule)]">
              {activeCatPerms.map(perm => {
                const checked = selectedPermissions.has(perm.id);
                return (
                  <label
                    key={perm.id}
                    className={`flex items-start gap-3 px-5 py-3 cursor-pointer transition-colors hover:bg-[var(--surface)] ${
                      checked ? 'bg-[var(--slate-dim)]/40' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggle(perm.id)}
                      className="mt-0.5 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-blue-500 flex-shrink-0"
                    />
                    <div className="min-w-0">
                      <div className={`text-sm font-medium ${checked ? 'text-[var(--text-pri)]' : 'text-[var(--text-sec)]'}`}>
                        {perm.label}
                      </div>
                      <div className="text-xs text-[var(--text-ter)] mt-0.5 leading-relaxed">{perm.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-[var(--rule)] flex-shrink-0 bg-[var(--surface)]">
          <span className="text-xs text-[var(--text-ter)]">
            <span className="font-medium text-[var(--text-sec)]">{selectedPermissions.size}</span> permission{selectedPermissions.size !== 1 ? 's' : ''} selected
          </span>
          <div className="flex gap-2">
            <button onClick={onClose} className="btn btn-secondary text-sm">Cancel</button>
            <button onClick={handleSubmit} className="btn btn-primary text-sm">
              {isEditing ? 'Update Role' : 'Create Role'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Scope Node Picker — helpers
// ============================================================================

interface AssignableScopeNode {
  id: string;
  scopeKind: ScopeKind;
  label: string;
  sublabel?: string;
}

function buildAssignableScopeNodes(
  workspaces: Array<{ id: string; name: string }>,
  orgUnits: import('../../../types/prioritization/enterpriseIdentity').OrgUnit[],
  tenantId: string | null
): AssignableScopeNode[] {
  const nodes: AssignableScopeNode[] = [];

  nodes.push({
    id: tenantId ?? '*',
    scopeKind: 'organization',
    label: 'Entire organization',
    sublabel: 'Applies across all workspaces',
  });

  workspaces.forEach(ws => {
    nodes.push({
      id: ws.id,
      scopeKind: 'resource',
      label: ws.name,
      sublabel: 'Workspace',
    });
  });

  const sorted = [...orgUnits].sort((a, b) =>
    a.depth !== b.depth ? a.depth - b.depth : a.displayName.localeCompare(b.displayName)
  );
  sorted.forEach(unit => {
    const inferredKind: ScopeKind =
      unit.depth === 0 ? 'organization' :
      unit.depth === 1 ? 'department' :
      'team';
    nodes.push({
      id: unit.id,
      scopeKind: inferredKind,
      label: unit.displayName,
      sublabel: unit.type || undefined,
    });
  });

  return nodes;
}

function ScopeNodePicker({
  nodes,
  selectedId,
  onSelect,
}: {
  nodes: AssignableScopeNode[];
  selectedId: string;
  onSelect: (node: AssignableScopeNode) => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = nodes.filter(n =>
    !search || n.label.toLowerCase().includes(search.toLowerCase())
  );
  const selected = nodes.find(n => n.id === selectedId);

  return (
    <div>
      <div className="text-xs font-medium text-[var(--text-ter)] mb-1">Where does this role apply?</div>
      {selected && (
        <div className="text-xs text-[var(--text-sec)] mb-2">
          Selected: <span className="font-medium text-[var(--text-pri)]">{selected.label}</span>
          {selected.sublabel && <span className="text-[var(--text-ter)]"> ({selected.sublabel})</span>}
        </div>
      )}
      <input
        type="text"
        placeholder="Search…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full border border-[var(--rule)] px-2.5 py-1.5 text-sm mb-1"
      />
      <div className="max-h-40 overflow-y-auto border border-[var(--rule)] divide-y divide-[var(--rule)]">
        {filtered.map(node => (
          <button
            key={node.id}
            type="button"
            onClick={() => onSelect(node)}
            className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--slate-dim)] flex items-center justify-between gap-2 ${
              selectedId === node.id ? 'bg-[var(--slate-dim)] border-l-2 border-[var(--slate)]' : ''
            }`}
          >
            <span className="font-medium text-[var(--text-pri)] truncate">{node.label}</span>
            {node.sublabel && (
              <span className="text-xs text-[var(--text-ter)] flex-shrink-0">{node.sublabel}</span>
            )}
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="text-xs text-[var(--text-ter)] italic px-3 py-2">No matching nodes</p>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Role Assignees Modal
// ============================================================================

interface RoleAssigneesModalProps {
  role: RoleWithDetails;
  onClose: () => void;
}

function RoleAssigneesModal({ role, onClose }: RoleAssigneesModalProps) {
  const [assignees, setAssignees] = useState<RoleAssignee[]>([]);
  const [loading, setLoading] = useState(true);
  const { orgUnits: orgUnitsMap, fetchFromSupabase: fetchOrgUnits } = useEnterpriseIdentityStore();
  const { currentTenantId, currentWorkspaceId, getWorkspacesForTenant } = useTenantStore();
  const currentRole = useUserStore(state => state.currentRole);
  const assignedRoleNames = useUserStore(state => state.assignedRoleNames);
  const adminScope = useMemo(
    () => resolveAdminScope(currentRole, assignedRoleNames),
    [currentRole, assignedRoleNames],
  );
  const orgUnitsList = useMemo(() => Array.from(orgUnitsMap.values()), [orgUnitsMap]);
  const workspaces = useMemo(
    () => (currentTenantId ? getWorkspacesForTenant(currentTenantId) : []),
    [currentTenantId, getWorkspacesForTenant],
  );
  const isWholeScopeRole = role.name === 'workspace-admin' || role.name === 'tenant-admin';

  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignType, setAssignType] = useState<'user' | 'group'>('user');
  const [availableUsers, setAvailableUsers] = useState<{ id: string; name: string }[]>([]);
  const [availableGroups, setAvailableGroups] = useState<{ id: string; name: string }[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [assignScopeKind, setAssignScopeKind] = useState<ScopeKind>('organization');
  const [assignScopeId, setAssignScopeId] = useState(currentTenantId ?? '*');

  const scopeNodes = useMemo(() =>
    buildAssignableScopeNodes(workspaces, orgUnitsList, currentTenantId),
    [workspaces, orgUnitsList, currentTenantId]
  );

  const getScopeDisplayName = (scopeKind: string, scopeId: string): string => {
    if (scopeKind === 'organization' && (scopeId === '*' || !scopeId)) return 'Whole tenant (organization)';
    if (scopeKind === 'resource' && (scopeId === '*' || !scopeId)) return 'Whole workspace';
    if (scopeKind === 'resource') {
      const ws = workspaces.find(w => w.id === scopeId);
      return ws?.name ?? scopeId.slice(0, 8) + '…';
    }
    if (scopeId === '*') return `All ${scopeKind}s`;
    const orgUnit = orgUnitsMap.get(scopeId);
    if (orgUnit) return orgUnit.displayName;
    return scopeId.slice(0, 8) + '…';
  };

  useEffect(() => {
    loadAssignees();
    loadAvailableSubjects();
    fetchOrgUnits();
    // The three loaders are re-created each render but do not close over anything
    // that should retrigger this; the effect is keyed on the role being viewed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role.id]);

  const loadAvailableSubjects = async () => {
    const supabase = getSupabaseClient();
    const scopedUsers = await fetchUsersForScope(adminScope, currentTenantId || undefined, currentWorkspaceId || undefined);
    let groupsQuery = supabase.from('groups').select('id, name').order('name');
    if (currentWorkspaceId) groupsQuery = groupsQuery.eq('workspace_id', currentWorkspaceId);
    const groupsRes = await groupsQuery;
    setAvailableUsers((scopedUsers || []).map(u => ({ id: u.id, name: u.name })));
    setAvailableGroups(groupsRes.data || []);
  };

  const loadAssignees = async () => {
    setLoading(true);
    try {
      const supabase = getSupabaseClient();
      const { data, error } = await supabase.from('role_assignments').select('*').eq('role_id', role.id);
      if (error) throw error;

      const userIds = (data || []).filter(a => a.subject_type === 'user').map(a => a.subject_id);
      const groupIds = (data || []).filter(a => a.subject_type === 'group').map(a => a.subject_id);

      const [usersResult, groupsResult] = await Promise.all([
        userIds.length > 0 ? supabase.from('users').select('id, name').in('id', userIds) : { data: [] },
        groupIds.length > 0 ? supabase.from('groups').select('id, name').in('id', groupIds) : { data: [] },
      ]);

      const userNames = new Map<string, string>();
      (usersResult.data || []).forEach(u => userNames.set(u.id, u.name));
      const groupNames = new Map<string, string>();
      (groupsResult.data || []).forEach(g => groupNames.set(g.id, g.name));

      setAssignees((data || []).map(a => ({
        id: a.id,
        subjectType: a.subject_type,
        subjectId: a.subject_id,
        subjectName: a.subject_type === 'user' ? userNames.get(a.subject_id) || 'Unknown User' : groupNames.get(a.subject_id) || 'Unknown Group',
        scopeKind: a.scope_kind || 'global',
        scopeId: a.scope_id || '*',
      })));
    } catch (err) {
      console.error('Failed to load assignees:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedSubjectId) { alert('Please select a user or group'); return; }
    if (assigning) return;
    let scopeKind: ScopeKind = assignScopeKind;
    let scopeId = assignScopeId;
    if (isWholeScopeRole) {
      if (role.name === 'tenant-admin') {
        scopeKind = 'organization';
        scopeId = currentTenantId || '*';
      } else {
        scopeKind = 'resource';
        scopeId = currentWorkspaceId || workspaces[0]?.id || '*';
      }
    }
    setAssigning(true);
    try {
      await createRoleAssignment({
        subjectType: assignType,
        subjectId: selectedSubjectId,
        roleId: role.id,
        scopeKind,
        scopeId: scopeId || '*',
      }, useUserStore.getState().userId);
      setShowAssignForm(false);
      setSelectedSubjectId('');
      setAssignScopeKind('organization');
      setAssignScopeId(currentTenantId ?? '*');
      await loadAssignees();
    } catch (err) {
      alert(`Failed to assign: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setAssigning(false);
    }
  };

  const userAssignees = assignees.filter(a => a.subjectType === 'user');
  const groupAssignees = assignees.filter(a => a.subjectType === 'group');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-[var(--bg-raised)] shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--rule)] flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-pri)]">{role.label}</h2>
            <p className="text-xs text-[var(--text-ter)] mt-0.5">Assignees</p>
          </div>
          <button onClick={onClose} className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {/* Assign Button / Form */}
          {!showAssignForm ? (
            <button
              onClick={() => setShowAssignForm(true)}
              className="flex items-center gap-2 text-sm text-[var(--text-ter)] hover:text-[var(--text-ter)] font-medium"
            >
              <Plus className="h-4 w-4" /> Assign User or Group
            </button>
          ) : (
            <div className="bg-[var(--surface)] p-4 space-y-3 border border-[var(--rule)]">
              <div className="flex gap-2">
                {(['user', 'group'] as const).map(t => (
                  <button
                    key={t}
                    onClick={() => { setAssignType(t); setSelectedSubjectId(''); }}
                    className={`flex-1 py-1.5  text-sm font-medium transition-colors ${
                      assignType === t ? 'bg-[var(--slate)] text-white' : 'bg-[var(--bg-raised)] text-[var(--text-sec)] border border-[var(--rule)] hover:border-[var(--rule)]'
                    }`}
                  >
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </button>
                ))}
              </div>

              <select
                value={selectedSubjectId}
                onChange={e => setSelectedSubjectId(e.target.value)}
                className="w-full border border-[var(--rule)] px-3 py-1.5 text-sm bg-[var(--bg-raised)]"
              >
                <option value="">Select {assignType}…</option>
                {(assignType === 'user' ? availableUsers : availableGroups).map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              {assignType === 'group' && (
                <InlineGroupCreator
                  workspaceId={currentWorkspaceId}
                  onCreated={created => {
                    setAvailableGroups(prev => [...prev, { id: created.id, name: created.displayName }]);
                    setSelectedSubjectId(created.id);
                  }}
                />
              )}

              {isWholeScopeRole ? (
                <p className="text-xs text-[var(--text-ter)]">
                  Scope: {role.name === 'tenant-admin' ? 'Whole tenant (organization)' : 'Whole workspace'} — no de-scoping for this role.
                </p>
              ) : (
                <ScopeNodePicker
                  nodes={scopeNodes}
                  selectedId={assignScopeId}
                  onSelect={(node) => {
                    setAssignScopeKind(node.scopeKind);
                    setAssignScopeId(node.id);
                  }}
                />
              )}

              <div className="flex gap-2">
                <button
                  onClick={handleAssign}
                  disabled={!selectedSubjectId || assigning}
                  className="flex-1 py-1.5 bg-[var(--slate)] text-white text-sm font-medium hover:bg-[var(--slate)] disabled:bg-[var(--hover-tint)] disabled:text-[var(--text-ter)]"
                >
                  {assigning ? 'Assigning…' : 'Assign'}
                </button>
                <button onClick={() => setShowAssignForm(false)} className="px-4 py-1.5 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2 text-[var(--text-ter)]">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span className="text-sm">Loading…</span>
            </div>
          ) : assignees.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)] text-center py-6">No users or groups assigned to this role.</p>
          ) : (
            <div className="space-y-4">
              {userAssignees.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <User className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <span className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Users ({userAssignees.length})</span>
                  </div>
                  <div className="space-y-1">
                    {userAssignees.map(a => (
                      <div key={a.id} className="flex items-center justify-between py-1.5 px-3 bg-[var(--surface)]">
                        <span className="text-sm text-[var(--text-pri)]">{a.subjectName}</span>
                        <span className="text-xs text-[var(--text-ter)]">{getScopeDisplayName(a.scopeKind, a.scopeId)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {groupAssignees.length > 0 && (
                <div>
                  <div className="flex items-center gap-1.5 mb-2">
                    <Users className="h-3.5 w-3.5 text-[var(--text-ter)]" />
                    <span className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Groups ({groupAssignees.length})</span>
                  </div>
                  <div className="space-y-1">
                    {groupAssignees.map(a => (
                      <div key={a.id} className="flex items-center justify-between py-1.5 px-3 bg-[var(--surface)]">
                        <span className="text-sm text-[var(--text-pri)]">{a.subjectName}</span>
                        <span className="text-xs text-[var(--text-ter)]">{getScopeDisplayName(a.scopeKind, a.scopeId)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-[var(--rule)] flex justify-end flex-shrink-0">
          <button onClick={onClose} className="btn btn-secondary text-sm">Close</button>
        </div>
      </div>
    </div>
  );
}
